/**
 * useCaptionEngine — real-time caption engine for the Worship Presenter.
 *
 * Two engines, hot-swappable:
 *   • "webspeech" — Chrome / Edge / most browsers with SpeechRecognition
 *   • "whisper"   — MediaRecorder → POST /admin/worship/captions/transcribe
 *
 * Web Speech runs entirely client-side (free, ~200ms latency) and is the
 * default. Whisper is an opt-in fallback / higher-accuracy mode; it
 * records 5-second webm chunks, uploads them, and appends the returned
 * transcript to the caption buffer.
 *
 * The hook exposes:
 *   { supported, isListening, engine, setEngine, start, stop, current, history }
 * and calls `onFinal(text)` every time a final transcript segment is
 * ready — the caller (the Presenter) forwards `text` on the
 * BroadcastChannel so open output windows show captions live.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const CHUNK_MS = 5000;                // whisper chunk length
const MAX_HISTORY = 30;               // caption buffer lines

export function isWebSpeechSupported() {
  return typeof window !== 'undefined'
    && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}

export default function useCaptionEngine({ language = 'en-US', onFinal } = {}) {
  const [engine, setEngine] = useState(isWebSpeechSupported() ? 'webspeech' : 'whisper');
  const [isListening, setIsListening] = useState(false);
  const [current, setCurrent] = useState('');
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  const recRef = useRef(null);       // SpeechRecognition
  const mrRef = useRef(null);        // MediaRecorder
  const streamRef = useRef(null);    // MediaStream
  const chunkTimerRef = useRef(null);
  const onFinalRef = useRef(onFinal);
  useEffect(() => { onFinalRef.current = onFinal; }, [onFinal]);

  const pushFinal = useCallback((text) => {
    if (!text || !text.trim()) return;
    const line = text.trim();
    setHistory((prev) => {
      const next = [...prev, { text: line, at: Date.now() }];
      return next.length > MAX_HISTORY ? next.slice(-MAX_HISTORY) : next;
    });
    setCurrent('');
    if (onFinalRef.current) onFinalRef.current(line);
  }, []);

  // ── WEB SPEECH ────────────────────────────────────────────────────
  const startWebSpeech = useCallback(() => {
    const Klass = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Klass) {
      setError('Web Speech API not supported in this browser');
      return;
    }
    const rec = new Klass();
    rec.lang = language;
    rec.interimResults = true;
    rec.continuous = true;
    rec.onresult = (ev) => {
      let interim = '';
      for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
        const r = ev.results[i];
        if (r.isFinal) pushFinal(r[0].transcript);
        else interim += r[0].transcript;
      }
      if (interim) setCurrent(interim);
    };
    rec.onerror = (ev) => {
      setError(`Web Speech error: ${ev.error || 'unknown'}`);
    };
    rec.onend = () => {
      // Auto-restart while still listening (some browsers stop after ~60s)
      if (recRef.current === rec && isListening) {
        try { rec.start(); } catch (e) { /* noop */ }
      }
    };
    recRef.current = rec;
    try {
      rec.start();
      setIsListening(true);
      setError(null);
    } catch (e) {
      setError(`Could not start recognition: ${e.message}`);
    }
  }, [language, isListening, pushFinal]);

  const stopWebSpeech = useCallback(() => {
    if (recRef.current) {
      try { recRef.current.stop(); } catch (e) { /* noop */ }
      recRef.current = null;
    }
    setIsListening(false);
    setCurrent('');
  }, []);

  // ── WHISPER (chunked MediaRecorder → /captions/transcribe) ─────────
  const uploadChunk = useCallback(async (blob) => {
    if (!blob || blob.size < 2000) return;
    const form = new FormData();
    form.append('audio', blob, 'chunk.webm');
    try {
      const r = await axios.post(`${API}/api/admin/worship/captions/transcribe`, form, {
        headers: { ...authH() },
        timeout: 30000,
      });
      if (r.data?.text) pushFinal(r.data.text);
    } catch (e) {
      setError(`Whisper: ${e?.response?.data?.detail || e.message}`);
    }
  }, [pushFinal]);

  const startWhisper = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mrRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data && e.data.size) uploadChunk(e.data); };
      mr.start();
      // Rotate every CHUNK_MS: stop + restart so each `dataavailable` fires with a full chunk
      chunkTimerRef.current = setInterval(() => {
        if (!mrRef.current || mrRef.current.state !== 'recording') return;
        try { mrRef.current.stop(); } catch (e) { /* noop */ }
        try {
          const nm = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
          mrRef.current = nm;
          nm.ondataavailable = (e) => { if (e.data && e.data.size) uploadChunk(e.data); };
          nm.start();
        } catch (e) { /* noop */ }
      }, CHUNK_MS);
      setIsListening(true);
      setError(null);
    } catch (e) {
      setError(`Mic access denied: ${e.message}`);
    }
  }, [uploadChunk]);

  const stopWhisper = useCallback(() => {
    if (chunkTimerRef.current) {
      clearInterval(chunkTimerRef.current);
      chunkTimerRef.current = null;
    }
    if (mrRef.current) {
      try { mrRef.current.stop(); } catch (e) { /* noop */ }
      mrRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setIsListening(false);
  }, []);

  // ── PUBLIC API ─────────────────────────────────────────────────────
  const start = useCallback(() => {
    if (isListening) return;
    if (engine === 'webspeech') startWebSpeech();
    else startWhisper();
  }, [engine, isListening, startWebSpeech, startWhisper]);

  const stop = useCallback(() => {
    if (engine === 'webspeech') stopWebSpeech();
    else stopWhisper();
  }, [engine, stopWebSpeech, stopWhisper]);

  const clearHistory = useCallback(() => { setHistory([]); setCurrent(''); }, []);

  useEffect(() => () => { stopWebSpeech(); stopWhisper(); }, [stopWebSpeech, stopWhisper]);

  return {
    supported: engine === 'webspeech' ? isWebSpeechSupported() : true,
    isListening,
    engine,
    setEngine: (e) => { if (isListening) stop(); setEngine(e); },
    start,
    stop,
    current,
    history,
    error,
    clearHistory,
  };
}
